import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  LeftProps,
  MinHeightProps,
  MinWidthProps,
  PositionProps,
  RightProps,
  SizeProps,
} from 'styled-system';
import {
  color,
  display,
  left,
  maxHeight,
  minHeight,
  minWidth,
  overflow,
  position,
  right,
  size,
  space,
  textAlign,
  width,
} from 'styled-system';

import { Flex } from 'common/components/Flex';
import Icon from 'common/components/Icon';
import { BodyRegularBR2, HeadlineH7 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import { boxSizing, lineClamp, rotate, textDecoration } from 'common/theme/system-utilities';

export interface ArticleEntity {
  name: string;
  url: string;
}

export enum NavigationType {
  Previous = 'PREVIOUS',
  Next = 'NEXT',
}

export interface ArticleProps extends ArticleEntity {
  type: NavigationType;
  withMaxWidth: boolean;
}

interface IconStyleProps
  extends PositionProps, RightProps, LeftProps, MinWidthProps, MinHeightProps, SizeProps {
  rotate?: string;
}

const ICON_SIZE = '24px';
const ROTATION_ANGLE = '180';
const NEXT_CONTAINER_STYLED_PROPS = {
  pr: 'sp_32',
  pl: ['sp_8', 'sp_12', null, 'sp_16'],
  textAlign: 'right',
};
const PREV_ARTICLE_STYLED_PROPS = {
  flexDirection: 'row-reverse',
  textAlign: 'left',
};
const PREV_ARROW_STYLED_PROPS = {
  rotate: ROTATION_ANGLE,
  left: 0,
};

const ArticleContainer = styled.li<any>`
  ${position};
  ${width};
  ${textAlign};
  ${space};
  ${boxSizing};
  ${overflow};
  list-style: none;
  &:first-of-type {
    margin-left: 0;
  }
  &:last-of-type {
    margin-right: 0;
  }
`;

const articleContainerStyles: ComponentProps<typeof ArticleContainer> = {
  position: 'relative',
  width: '90%',
  overflow: ['hidden', 'visible'],
  boxSizing: 'border-box',
  pl: 'sp_32',
  pr: ['sp_8', 'sp_12', null, 'sp_16'],
};

const labelStyles: ComponentProps<typeof BodyRegularBR2> = {
  mb: ['sp_4', 'sp_8'],
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
};

const StyledFlex = styled(Flex)<any>`
  ${color};
  ${maxHeight};
  ${textDecoration};
`;

const flexStyles: ComponentProps<typeof StyledFlex> = {
  as: 'a',
  alignItems: 'center',
  color: 'sys.neutral.border.strong',
  textDecoration: 'none',
  textAlign: 'right',
};

const ArticleTitle = styled(HeadlineH7)<any>`
  ${display};
  ${width};
  ${lineClamp};
  ${maxHeight};
  -webkit-box-orient: vertical;
`;

const articleTitleStyles: ComponentProps<typeof ArticleTitle> = {
  display: ['-webkit-box', 'block'],
  width: 1,
  overflow: [null, 'hidden'],
  whiteSpace: [null, 'nowrap'],
  textOverflow: 'ellipsis',
  lineClamp: ['2', '1'],
};

const ArrowRight = styled(Icon)<IconStyleProps>`
  ${size};
  ${minWidth};
  ${minHeight};
  ${rotate};
  ${position};
  ${right};
  ${left};

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    bottom: 0;
  }
`;

const arrowRightStyles: IconStyleProps = {
  position: 'absolute',
  right: 0,
  minWidth: ICON_SIZE,
  minHeight: ICON_SIZE,
  size: ICON_SIZE,
};

const Article = ({ type, name, url, withMaxWidth }: ArticleProps) => {
  const { t } = useLocalization();
  const navigationLabel =
    type === NavigationType.Previous ? 'h24_previous_article' : 'h24_next_article';

  return (
    <ArticleContainer
      {...articleContainerStyles}
      {...(type === NavigationType.Next ? NEXT_CONTAINER_STYLED_PROPS : {})}
      {...(withMaxWidth ? { width: '50%' } : {})}
    >
      <BodyRegularBR2 {...labelStyles}>{t(navigationLabel)}</BodyRegularBR2>
      <StyledFlex
        href={url}
        {...flexStyles}
        {...(type === NavigationType.Previous ? PREV_ARTICLE_STYLED_PROPS : {})}
      >
        <ArticleTitle {...articleTitleStyles}>{name}</ArticleTitle>
        <ArrowRight
          name="arrowRight"
          {...arrowRightStyles}
          {...(type === NavigationType.Previous ? PREV_ARROW_STYLED_PROPS : {})}
        />
      </StyledFlex>
    </ArticleContainer>
  );
};

export default Article;
